/**
 * Forecast card header: title + part count + snapshot freshness on the left;
 * the quick search on the right. CR-6448: Refresh, Export and Filters moved to
 * the filter panel's own header (`ForecastFiltersPanel`), matching Deliveries,
 * so this bar no longer carries a second copy of them.
 */
import { CardTitle } from '../../../ui/card';
import { Search, TrendingUp } from 'lucide-react';
import { ForecastRefreshStatus } from './useForecastSnapshotRefresh';

interface ForecastToolbarProps {
  partsCount: number;
  refreshStatus: ForecastRefreshStatus | null;
  quickSearch: string;
  quickSearchPlaceholder: string;
  onQuickSearchChange: (value: string) => void;
}

export function ForecastToolbar({
  partsCount, refreshStatus, quickSearch, quickSearchPlaceholder, onQuickSearchChange,
}: ForecastToolbarProps) {
  const lastRefreshed = refreshStatus?.last_refreshed_at
    ? new Date(refreshStatus.last_refreshed_at).toLocaleString()
    : null;
  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-indigo-600" />
          <CardTitle>Forecast Schedule</CardTitle>
          <span className="text-sm text-muted-foreground ml-2">
            ({partsCount.toLocaleString()} parts)
          </span>
        </div>
        {refreshStatus?.enabled && lastRefreshed && (
          <span className="text-[11px] text-muted-foreground ml-7 mt-0.5">
            Last refreshed {lastRefreshed}
            {refreshStatus.last_status === 'failed' && (
              <span className="text-red-600 ml-2">· last refresh failed</span>
            )}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Quick part search — debounced by the caller, applies independently
            of the Filters drawer. Matches the customer part number, the
            supplier's own reference, a contributing open PO number OR the
            supplier's name (API param part_search): a code off a delivery
            note may be any of them, and a buyer chasing one supplier types
            the name. */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={quickSearch}
            placeholder={quickSearchPlaceholder}
            aria-label="Quick search by part number, supplier part number, PO number or supplier name"
            onChange={(e) => onQuickSearchChange(e.target.value)}
            className="pl-8 pr-2 py-1.5 text-sm border rounded-lg w-80"
          />
        </div>
      </div>
    </div>
  );
}
